import { Injectable } from '@angular/core';
import { AccountInfo } from '@azure/msal-browser';
import { getUserInitialsFromAccountInfo } from '@seu-helpers';
import {
  SeuActiveCalculation,
  SeuCalculationResponseItem,
  SeuConfig,
  SeuConfigTranslationObject,
  SeuInterconnection,
  SeuOffer,
  SeuUser,
} from '@seu-types';
import {
  BehaviorSubject,
  debounceTime,
  distinctUntilChanged,
  filter,
} from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class StoreService {
  private user$$ = new BehaviorSubject<SeuUser | null | undefined>(undefined);

  public user$ = this.user$$.asObservable().pipe(
    filter((user): user is SeuUser | null => user !== undefined),
    distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
  );

  private activeOffer$$ = new BehaviorSubject<SeuOffer | undefined | null>(
    undefined
  );

  public activeOffer$ = this.activeOffer$$.pipe(
    filter((offer): offer is SeuOffer | null => offer !== undefined),
    debounceTime(10),
    distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
  );

  private activeInterconnection$$ = new BehaviorSubject<
    SeuInterconnection | undefined | null
  >(undefined);

  public activeInterconnection$ = this.activeInterconnection$$.pipe(
    filter(
      (interconnection): interconnection is SeuInterconnection | null =>
        interconnection !== undefined
    )
  );

  private config$$ = new BehaviorSubject<SeuConfig | undefined>(undefined);

  public config$ = this.config$$.pipe(
    distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
  );

  private activeCalculations$$ = new BehaviorSubject<SeuActiveCalculation[]>(
    []
  );

  public activeCalculations$ = this.activeCalculations$$.asObservable();

  private calculationResponseItems$$ = new BehaviorSubject<
    SeuCalculationResponseItem[]
  >([]);

  public calculationResponseItems$ =
    this.calculationResponseItems$$.asObservable();

  private activeCalculationResponseItem$$ =
    new BehaviorSubject<SeuCalculationResponseItem | null>(null);

  public activeCalculationResponseItem$ =
    this.activeCalculationResponseItem$$.asObservable();

  public setUserFromAccountInfo(accountInfo: AccountInfo | null): void {
    if (!accountInfo) {
      this.user$$.next(null);
      return;
    }
    this.user$$.next({
      accountInfo,
      name: accountInfo.name ?? '',
      initials: getUserInitialsFromAccountInfo(accountInfo),
    });
  }

  public setUser(user: SeuUser | null): void {
    this.user$$.next(user);
  }

  public getUser(): SeuUser | null | undefined {
    return this.user$$.getValue();
  }

  public setActiveOffer(activeOffer: SeuOffer | null): void {
    this.activeOffer$$.next(activeOffer);
  }

  public setActiveInterconnection(
    activeInterconnection: SeuInterconnection | null
  ): void {
    this.activeInterconnection$$.next(activeInterconnection);
  }

  public updateActiveInterconnection(
    activeInterconnection: SeuInterconnection | null
  ): void {
    const curInterconnection = this.activeInterconnection$$.getValue();
    if (curInterconnection?.id === activeInterconnection?.id) {
      this.activeInterconnection$$.next(activeInterconnection);
    }
  }

  public setConfig(config: SeuConfig): void {
    this.config$$.next(config);
  }

  public getConfig(): SeuConfig {
    return this.config$$.getValue()!;
  }

  public getConfigTranslations(): SeuConfigTranslationObject | undefined {
    const config = this.config$$.getValue();
    if (!config) {
      return;
    }
    return config.translations.de;
  }

  public setActiveCalculations(
    activeCalculations: SeuActiveCalculation[]
  ): void {
    this.activeCalculations$$.next(activeCalculations);
  }

  public addCalculationResponseItem(
    calculationResponseItem: SeuCalculationResponseItem
  ): void {
    const calculationResponseItems = this.calculationResponseItems$$.getValue();
    calculationResponseItems.push(calculationResponseItem);
    this.calculationResponseItems$$.next(calculationResponseItems);
  }

  public resetCalculationResponseItems(): void {
    this.calculationResponseItems$$.next([]);
  }

  public setActiveCalculationResponseItem(
    activeCalculationResponseItem: SeuCalculationResponseItem | null
  ): void {
    this.activeCalculationResponseItem$$.next(activeCalculationResponseItem);
  }
}
